import React from 'react';
import { ScanBarcode, Languages, Sun, Moon, BookOpen, Menu, Heart, Sparkles } from 'lucide-react';
import { useLang } from '../i18n';
import { useTheme } from '../theme';
import { trackBookClick } from '../bookData';
import { AccountMenu } from './AccountMenu';

export const Header = ({ onMenuClick }) => {
  const { lang, setLang, t } = useLang();
  const { resolved, toggleTheme } = useTheme();
  return (
    <header
      className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60"
      data-testid="app-header"
    >
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(900px_circle_at_15%_0%,rgba(37,99,235,0.12),transparent_55%)]" />
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={onMenuClick}
            data-testid="nav-menu-toggle"
            aria-label={lang === 'en' ? 'Open menu' : 'Menü öffnen'}
            title={lang === 'en' ? 'Menu' : 'Menü'}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-foreground transition-colors duration-150 hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
            <ScanBarcode className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold leading-tight text-foreground sm:text-xl">
              {t('app_title')}
            </h1>
            <p className="hidden text-xs text-muted-foreground sm:block">{t('app_subtitle')}</p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <AccountMenu />
          <a
            href="/supporter"
            data-testid="header-supporter-btn"
            title={lang === 'en' ? 'Support us' : 'Unterstützen'}
            aria-label={lang === 'en' ? 'Support Barcode Studio' : 'Barcode Studio unterstützen'}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/10 px-2.5 py-1.5 text-sm font-semibold text-primary shadow-sm transition-colors duration-150 hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Heart className="h-4 w-4" />
            <span className="hidden sm:inline">{lang === 'en' ? 'Support' : 'Unterstützen'}</span>
          </a>
          <a
            href="/?buch=1"
            data-testid="header-book-link"
            onClick={() => { try { trackBookClick('header_link'); } catch (e) {} }}
            title={lang === 'en' ? 'Book tip: Funkelmoor' : 'Buchtipp: Funkelmoor'}
            aria-label={lang === 'en' ? 'Book tip: Die Wolkenpost von Funkelmoor' : 'Buchtipp: Die Wolkenpost von Funkelmoor'}
            className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/10 px-2.5 py-1.5 text-sm font-medium text-primary shadow-sm transition-colors duration-150 hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">{lang === 'en' ? 'Book' : 'Buch'}</span>
          </a>
          <a
            href={lang === 'en' ? '/en/guides/' : '/ratgeber/'}
            data-testid="header-guides-link"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border bg-card px-2.5 py-1.5 text-sm font-medium text-muted-foreground shadow-sm transition-colors duration-150 hover:text-foreground hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <BookOpen className="h-4 w-4" />
            <span className="hidden sm:inline">{lang === 'en' ? 'Guides' : 'Ratgeber'}</span>
          </a>
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            aria-label={t('toggle_theme')}
            title={t('toggle_theme')}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border bg-card text-muted-foreground shadow-sm transition-colors duration-150 hover:text-foreground hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {resolved === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Languages className="hidden h-4 w-4 text-muted-foreground sm:block" />
          <div className="inline-flex items-center rounded-lg border bg-card p-0.5 shadow-sm">
            {['de', 'en'].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`lang-${l}`}
                className={`rounded-md px-2.5 py-1 text-sm font-medium transition-colors duration-150 sm:px-3 ${
                  lang === l
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
};
